import { FileExtension } from "@inrev/common";
import {
	type FieldPathByValue,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { cn } from "../../lib/utils";
import { ClickableIcon, Icon } from "../../primitives";
import { Dropzone } from "../../ui/Dropzone";
import { FormFieldError } from "../FormFieldError";

const PDF_ONLY: FileExtension[] = [FileExtension.pdf];

export type FormFileUploadProps<
	TFieldValues extends FieldValues,
	TName extends FieldPathByValue<TFieldValues, File | undefined> = FieldPathByValue<
		TFieldValues,
		File | undefined
	>,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	disabled?: boolean;
	errorMessage?: string;
	placeholderText?: string;
	testId?: string;
};

export const FormFileUpload = <
	TFieldValues extends FieldValues,
	TName extends FieldPathByValue<TFieldValues, File | undefined> = FieldPathByValue<
		TFieldValues,
		File | undefined
	>,
	TTransformedValues = TFieldValues,
>({
	name,
	control,
	defaultValue,
	disabled,
	errorMessage,
	placeholderText,
	testId,
}: FormFileUploadProps<TFieldValues, TName, TTransformedValues>) => {
	const {
		field: { value: file, onChange, onBlur },
		fieldState: { error },
	} = useController<TFieldValues, TName, TTransformedValues>({ name, control, defaultValue });

	const hasError = !!error;

	const handleSuccess = (files: File[]) => {
		if (files.length === 0) return;
		onChange(files[0]);
		onBlur();
	};

	const handleDelete = () => {
		onChange(undefined);
		onBlur();
	};

	return (
		<div className="relative">
			{file ? (
				<div
					data-testid={testId}
					className={cn(
						"w-full flex items-center gap-x-[10px] px-[15px] py-[12px] rounded-md border bg-surface-base",
						hasError ? "border-danger" : "border-bc-default",
					)}
				>
					<Icon type="document-text" size="small" color="secondary" />
					<span className="flex-1 truncate text-[14px] text-primary">{file.name}</span>
					{!disabled && (
						<ClickableIcon
							type="trash"
							size="small"
							onClick={handleDelete}
							className="shrink-0 text-tertiary hover:text-danger"
							aria-label="Remove file"
						/>
					)}
				</div>
			) : (
				<Dropzone
					allowedExtensions={PDF_ONLY}
					onSuccess={handleSuccess}
					disabled={disabled}
					placeholderText={placeholderText}
					className={cn("!mb-0", hasError ? "border-danger" : undefined)}
				/>
			)}
			{hasError && <FormFieldError error={error} errorMessage={errorMessage} />}
		</div>
	);
};
